<div class="knowledge-base-widget">
  <!-- Header -->
  <div class="kb-header">
    <div class="header-content">
      <div class="header-icon">📚</div>
      <div class="header-text">
        <h3>Knowledge Base Assistant</h3>
        <span class="header-status">IAM Authentication</span>
      </div>
    </div>
    <div class="header-actions">
      <button class="header-btn" (click)="toggleSettings()" title="Settings">
        ⚙️
      </button>
      <button class="header-btn" (click)="clearChat()" title="Clear chat">
        🗑️
      </button>
      <button class="header-btn close-btn" (click)="closeWidget()" title="Close">
        ✕
      </button>
    </div>
  </div>

  <!-- Main Content: Two Column Layout -->
  <div class="kb-main-content" [class.settings-open]="showSettings">
    <!-- Left Column: Configuration Panel -->
    <div class="config-panel" *ngIf="showSettings">
      <div class="config-section">
        <h4>🏢 Company Selection</h4>
        <select
          [(ngModel)]="selectedCompanyId"
          (change)="onCompanyChange()"
          class="form-control"
        >
          <option value="">-- Select Company --</option>
          <option *ngFor="let company of companies" [value]="company.id">
            {{ company.name }}
          </option>
        </select>
        <small class="hint">Select a company for document filtering (required)</small>
        <div *ngIf="!selectedCompanyId || !tenantId.trim()" class="warning-box">
          ⚠️ No company selected - this may cause security errors
        </div>
        <div *ngIf="companies.length === 0" class="info-box">
          <small>Loading companies...</small>
        </div>
      </div>

      <div class="config-section">
        <h4>👥 Role-Based Access Control</h4>
        <div class="info-box">
          <small>✅ Roles are automatically determined based on your user account. No manual selection needed.</small>
        </div>
      </div>

      <div class="config-section">
        <h4>📚 Topic Access</h4>
        <small class="hint">Select topics you have access to:</small>
        <div class="topic-selectors">
          <label>Speaker Topics:</label>
          <select
            [(ngModel)]="speakerTopic"
            class="form-control"
          >
            <option value="">-- Select Speaker Topic --</option>
            <option *ngFor="let topic of availableTopics" [value]="topic">
              {{ topic }}
            </option>
          </select>
          <label style="margin-top: 12px;">Attendee Topics:</label>
          <select
            [(ngModel)]="attendeeTopic"
            class="form-control"
          >
            <option value="">-- Select Attendee Topic --</option>
            <option *ngFor="let topic of availableTopics" [value]="topic">
              {{ topic }}
            </option>
          </select>
        </div>
      </div>

      <div class="config-section">
        <h4>🔍 Additional Filters</h4>
        <input
          type="text"
          [(ngModel)]="topic"
          class="form-control"
          placeholder="Topic (Optional - Legacy)"
        />
        <small class="hint">Filter by topic/subject (legacy field)</small>
      </div>

      <div class="config-section">
        <h4>🔍 Search Parameters</h4>
        <div class="param-row">
          <label>Number of Results:</label>
          <input
            type="number"
            [(ngModel)]="numberOfResults"
            min="1"
            max="20"
            class="form-control"
          />
        </div>
        <div class="param-row">
          <label>Score Threshold:</label>
          <input
            type="number"
            [(ngModel)]="score"
            min="0"
            max="1"
            step="0.1"
            class="form-control"
          />
        </div>
      </div>

      <div class="config-section">
        <h4>🔗 Connection Test</h4>
        <button
          class="btn btn-primary"
          (click)="testConnection()"
          [disabled]="isTestingConnection || !tenantId.trim()"
        >
          {{ isTestingConnection ? '🔄 Testing...' : '🔗 Test Connection' }}
        </button>
        <div *ngIf="connectionTestResult" class="connection-result">
          <pre>{{ connectionTestResult }}</pre>
        </div>
      </div>

      <div class="config-section">
        <h4>🔑 Session Management</h4>
        <div class="session-buttons">
          <button class="btn btn-secondary" (click)="newSession()">
            🔄 New Session
          </button>
          <button class="btn btn-secondary" (click)="clearChat()">
            🗑️ Clear Chat
          </button>
        </div>
        <div class="session-info">
          <small>🆔 Session ID: {{ sessionId.substring(0, 20) }}...</small>
          <small>💬 Messages: {{ messages.length }}</small>
        </div>
      </div>

      <div class="config-section">
        <h4>🔍 Metadata Preview</h4>
        <div class="metadata-preview">
          <pre>{{ getMetadataPreview() | json }}</pre>
        </div>
      </div>

      <div class="config-section">
        <label class="checkbox-label">
          <input type="checkbox" [(ngModel)]="debugMode" />
          <span>🐛 Debug Mode</span>
        </label>
      </div>
    </div>

    <!-- Right Column: Chat Interface -->
    <div class="chat-panel">
      <div class="chat-header-info" *ngIf="!showSettings">
        <small>
          🆔 Session: {{ sessionId.substring(0, 20) }}... |
          🏢 Company: {{ getSelectedCompanyName() }} |
          👥 Roles: Auto-detected from your account
        </small>
      </div>

      <!-- Messages Area -->
      <div class="messages-container">
        <div class="messages">
          <div
            *ngFor="let message of messages"
            class="message"
            [class.user-message]="message.role === 'user'"
            [class.assistant-message]="message.role === 'assistant'"
          >
            <div class="message-avatar">
              {{ message.role === 'user' ? '👤' : '🤖' }}
            </div>
            <div class="message-bubble">
              <div
                class="message-content"
                [innerHTML]="renderMarkdown(message.content, message.role)"
              ></div>
              <div class="message-time">
                {{ message.timestamp | date: 'short' }}
              </div>
              <!-- Sources -->
              <div *ngIf="message.sources && message.sources.length > 0" class="message-sources">
                <details>
                  <summary>📚 Sources ({{ message.sources.length }})</summary>
                  <div class="sources-list">
                    <div *ngFor="let source of message.sources; let i = index" class="source-item">
                      <strong>{{ i + 1 }}. {{ source.filename || 'Source ' + (i + 1) }}</strong>
                      <div *ngIf="source.location || source.uri || source.s3Location" class="source-location">
                        📍 Location: {{ source.location || source.uri || source.s3Location }}
                      </div>
                      <div *ngIf="source.score !== undefined" class="source-score">
                        Relevance Score: {{ source.score | number: '1.3-3' }}
                      </div>
                      <div *ngIf="source.content_preview" class="source-preview">
                        Preview: {{ source.content_preview.substring(0, 200) }}...
                      </div>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>

          <!-- Loading indicator -->
          <div *ngIf="isLoading" class="message assistant-message loading">
            <div class="message-avatar">🤖</div>
            <div class="message-bubble">
              <div class="message-content">
                <div class="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="input-container">
        <textarea
          [(ngModel)]="userInput"
          (keypress)="onKeyPress($event)"
          placeholder="Ask a question about your knowledge base..."
          [disabled]="isLoading"
          rows="1"
        ></textarea>
        <button
          (click)="sendMessage()"
          [disabled]="!userInput.trim() || isLoading"
          class="send-btn"
        >
          <span *ngIf="!isLoading">📤</span>
          <span *ngIf="isLoading" class="loading-spinner">⏳</span>
        </button>
      </div>
    </div>
  </div>
</div>

